import { GeometryType } from '../../../../geometry/GeometryType';
import { buildModelBodyReplacementMetadata } from '../../../../model-body/model-body-metadata';
import type { GeometryState } from '../../../engine/state';
import {
  clonePlainData,
  resolveBlendSourceObject,
  type BlendFeatureBuildInput,
  type BlendFeatureBuildResult,
} from './common';
import { buildBlendResult } from './blend-result';
import { BlendCommitShapeValidator } from './BlendCommitShapeValidator';
import { defaultChamferSolverRegistry } from './chamfer/registry/ChamferSolverRegistry';
import { ChamferPreviewGuideBuilder } from './chamfer/preview/ChamferPreviewGuideBuilder';
import { flattenChamferDefinitionItems } from './chamfer/functions/flattenChamferDefinitionItems';
import { resolveFaceFaceChamferMode } from './chamfer/functions/faceFaceChamferGeometry';
import { resolveChamferPartialPreviewDefinition } from './partial-preview';
import { prepareChamferDefinitionForBuild } from './solver-preparation';
import { resolveOffsetFaceChamferMode } from '../../../../utils/feature/normalizeChamferFeatureDefinition';

type PuppyKernel = ReturnType<typeof import('../../../engine/loader').getPuppyKernel>;

type SolidGeometryObject = GeometryObject & {
  type: GeometryType.SOLID;
  shape?: unknown;
  data?: Record<string, unknown>;
};

/** 倒角预览引导构建器。 */
const chamferPreviewGuideBuilder = new ChamferPreviewGuideBuilder();

/**
 * 校验倒角定义是否满足最低提交条件。
 *
 * @param definition 倒角定义。
 * @returns 是否有效。
 */
export function validateChamferDefinition(definition: ChamferFeatureDefinition): boolean {
  const items = flattenChamferDefinitionItems(definition);
  if (!definition || !Array.isArray(items) || items.length === 0) {
    return false;
  }
  if (!items.every((item) => item.kind === 'edge' || item.kind === 'vertex' || item.kind === 'face')) {
    return false;
  }

  switch (definition.type) {
    case 'angle-distance':
      return typeof definition.distance === 'number'
        && Number.isFinite(definition.distance)
        && definition.distance > 0
        && typeof definition.angleDeg === 'number'
        && Number.isFinite(definition.angleDeg)
        && definition.angleDeg >= 0
        && definition.angleDeg <= 180;
    case 'distance-distance':
      return typeof definition.distance === 'number'
        && Number.isFinite(definition.distance)
        && definition.distance > 0
        && (
          definition.symmetric !== false
          || (
            typeof definition.distance2 === 'number'
            && Number.isFinite(definition.distance2)
            && definition.distance2 > 0
          )
        );
    case 'offset-face': {
      const mode = resolveOffsetFaceChamferMode(definition);
      return items.every((item) => item.kind === 'edge' || item.kind === 'face')
        && (mode === 'symmetric' || items.every((item) => item.kind === 'edge'))
        && typeof definition.distance === 'number'
        && Number.isFinite(definition.distance)
        && definition.distance > 0
        && (
          mode === 'symmetric'
          || (
            typeof definition.distance2 === 'number'
            && Number.isFinite(definition.distance2)
            && definition.distance2 > 0
          )
        );
    }
    case 'vertex':
      return items.length === 1
        && items[0]?.kind === 'vertex'
        && (
          (definition.equalDistance === true
            && typeof definition.distance === 'number'
            && Number.isFinite(definition.distance)
            && definition.distance > 0)
          || (
            Array.isArray(definition.vertexDistances)
            && definition.vertexDistances.length === 3
            && definition.vertexDistances.every((distance) => Number.isFinite(distance) && distance > 0)
          )
        );
    case 'face-face':
      return validateFaceFaceChamferDefinition(definition);
    default:
      return false;
  }
}

/**
 * 校验 Face Face 倒角定义。
 *
 * @param definition 倒角定义。
 * @returns 是否满足提交条件。
 */
function validateFaceFaceChamferDefinition(definition: ChamferFeatureDefinition): boolean {
  const mode = resolveFaceFaceChamferMode(definition);
  return Boolean(
    definition.faceSets?.find((faceSet) => faceSet.setId === 'faceSet1')?.items?.length
    && definition.faceSets?.find((faceSet) => faceSet.setId === 'faceSet2')?.items?.length
      && (
        mode === 'chord-width'
          ? (
            typeof definition.width === 'number'
            && Number.isFinite(definition.width)
            && definition.width > 0
          )
          : (
            typeof definition.distance === 'number'
            && Number.isFinite(definition.distance)
            && definition.distance > 0
          )
      )
    && (
      mode !== 'asymmetric'
      || (
        typeof definition.distance2 === 'number'
        && Number.isFinite(definition.distance2)
        && definition.distance2 > 0
      )
    ),
  );
}

/**
 * 解析倒角定义中的最大线性参数（距离/宽度）。
 *
 * @param definition 倒角定义。
 * @returns 最大参数；无法解析时返回 null。
 */
function resolveChamferMaxParameter(definition: ChamferFeatureDefinition): number | null {
  const values: number[] = [];
  if (typeof definition.distance === 'number' && Number.isFinite(definition.distance) && definition.distance > 0) {
    values.push(definition.distance);
  }
  if (typeof definition.distance2 === 'number' && Number.isFinite(definition.distance2) && definition.distance2 > 0) {
    values.push(definition.distance2);
  }
  if (typeof definition.width === 'number' && Number.isFinite(definition.width) && definition.width > 0) {
    values.push(definition.width);
  }
  if (Array.isArray(definition.vertexDistances)) {
    definition.vertexDistances.forEach((distance) => {
      if (Number.isFinite(distance) && distance > 0) {
        values.push(distance);
      }
    });
  }
  return values.length > 0 ? Math.max(...values) : null;
}

/**
 * 求解前校验倒角参数是否超出模型尺寸。
 *
 * @param input 校验输入。
 */
function assertChamferParametersBeforeBuild(input: {
  puppy: PuppyKernel;
  sourceShape: unknown;
  definition: ChamferFeatureDefinition;
}): void {
  const maxParameter = resolveChamferMaxParameter(input.definition);
  if (maxParameter === null) {
    return;
  }
  BlendCommitShapeValidator.assertParameterWithinSourceBounds(
    input.puppy,
    input.sourceShape,
    maxParameter,
    'chamfer',
  );
}

/**
 * 求解后校验倒角结果 Shape。
 *
 * @param input 校验输入。
 */
function assertChamferResultShape(input: {
  puppy: PuppyKernel;
  sourceShape: unknown;
  resultShape: unknown;
}): void {
  BlendCommitShapeValidator.assertValidResultShape(input.puppy, input.resultShape, 'chamfer', {
    sourceShape: input.sourceShape,
  });
}

/**
 * 构建倒角特征结果。
 *
 * @param input 倒角特征构建输入。
 * @returns 倒角构建结果。
 */
export function buildChamferFeature(
  input: BlendFeatureBuildInput<ChamferFeatureDefinition>,
): BlendFeatureBuildResult {
  const originalDefinitionItems = flattenChamferDefinitionItems(input.definition);
  const { sourceObject, sourceObjectId } = resolveBlendSourceObject(
    input.state,
    input.upstreamBodyObjectId,
    originalDefinitionItems,
  );
  const sourceShape = sourceObject.shape;
  const preparedDefinition = prepareChamferDefinitionForBuild({
    puppy: input.puppy,
    sourceShape,
    sourceMetadata: sourceObject.metadata ?? {},
    definition: input.definition,
  });
  const definitionItems = flattenChamferDefinitionItems(preparedDefinition);
  assertChamferParametersBeforeBuild({
    puppy: input.puppy,
    sourceShape,
    definition: preparedDefinition,
  });
  const resultShape = defaultChamferSolverRegistry.buildShape({
    puppy: input.puppy,
    sourceShape,
    definition: preparedDefinition,
    sourceMetadata: sourceObject.metadata ?? {},
    sourceObjectId,
  });
  assertChamferResultShape({
    puppy: input.puppy,
    sourceShape,
    resultShape,
  });

  return buildBlendResult({
    puppy: input.puppy,
    featureId: input.featureId,
    shape: resultShape,
    sourceObjectId,
    definitionItems,
  });
}

/**
 * 创建倒角结果对象。
 *
 * @param input 创建输入。
 * @returns 结果几何对象。
 */
export function createChamferResultObject(input: {
  featureId: string;
  sourceObjectId: string;
  resultObjectId?: string;
  definition: ChamferFeatureDefinition;
  shape: unknown;
  topologyProvenance: BlendFeatureBuildResult['topologyProvenance'];
  sourceObject?: GeometryObject | null;
}): SolidGeometryObject {
  const objectId = input.resultObjectId ?? input.featureId;
  const replacementMetadata = buildModelBodyReplacementMetadata(input.sourceObject);
  return {
    id: objectId,
    type: GeometryType.SOLID,
    metadata: {
      name: '倒角',
      createdAt: Date.now(),
      featureId: input.featureId,
      featureKind: 'chamfer',
      sourceObjectId: input.sourceObjectId,
      topologyProvenance: input.topologyProvenance,
      ...replacementMetadata,
    },
    commandId: input.featureId,
    shape: input.shape,
    data: {
      upstreamBodyObjectId: input.sourceObjectId,
      definition: clonePlainData(input.definition),
    },
  };
}

/**
 * 构建倒角预览结果。
 *
 * @param input 预览输入。
 * @returns 预览构建结果。
 */
export function previewChamferFeature(input: {
  puppy: PuppyKernel;
  state: GeometryState;
  request: ChamferPreviewRequest;
}): BlendFeatureBuildResult {
  const partialPreview = resolveChamferPartialPreviewDefinition(
    input.request.definition,
    input.request.partialCursor,
  );
  const originalDefinitionItems = flattenChamferDefinitionItems(partialPreview.definition);
  const { sourceObject, sourceObjectId } = resolveBlendSourceObject(
    input.state,
    input.request.upstreamBodyObjectId,
    originalDefinitionItems,
  );
  const sourceShape = sourceObject.shape;
  const sourceMetadata = sourceObject.metadata ?? {};
  const preparedDefinition = prepareChamferDefinitionForBuild({
    puppy: input.puppy,
    sourceShape,
    sourceMetadata,
    definition: partialPreview.definition,
  });
  const definitionItems = flattenChamferDefinitionItems(preparedDefinition);
  assertChamferParametersBeforeBuild({
    puppy: input.puppy,
    sourceShape,
    definition: preparedDefinition,
  });
  const resultShape = defaultChamferSolverRegistry.buildShape({
    puppy: input.puppy,
    sourceShape,
    definition: preparedDefinition,
    sourceMetadata,
    sourceObjectId,
  });
  assertChamferResultShape({
    puppy: input.puppy,
    sourceShape,
    resultShape,
  });
  const previewResult = buildBlendResult({
    puppy: input.puppy,
    featureId: input.request.editingFeatureId ?? 'chamfer-preview',
    shape: resultShape,
    sourceObjectId,
    definitionItems,
  });
  const blendGuides = chamferPreviewGuideBuilder.build({
    puppy: input.puppy,
    sourceObjectId,
    sourceShape,
    sourceMetadata,
    definition: preparedDefinition,
    preferredItemRef: input.request.preferredItemRef ?? partialPreview.targetRef,
  });

  return {
    ...previewResult,
    blendGuides,
    partialPreviewApplied: partialPreview.applied,
    partialPreviewTargetRef: partialPreview.targetRef,
  };
}
